import { getApiRequest } from "@/services/api-request";
import { HP_CLIENT_THEME, isHpClientTheme } from "./hp-client";

/** Public endpoint: resolves the print house from the request domain, no token needed. */
const GET_PRINT_HOUSE_BRAND_URL = "/v1/get-print-house-login-logo";

export interface PrintHouseBrand {
  theme: string | null;
  tenantId: string | null;
  loginLogo: string | null;
  name: string | null;
}

export const readPrintHouseBrand = (payload: any): PrintHouseBrand => {
  const brand = payload?.data?.data ?? payload?.data ?? payload;
  return {
    theme: brand?.theme ?? brand?.Theme ?? null,
    tenantId: brand?.tenantId ?? brand?.TenantId ?? null,
    loginLogo: brand?.loginLogo ?? brand?.LoginLogo ?? null,
    name: brand?.name ?? brand?.Name ?? null,
  };
};

export const resolveBrandThemeName = (
  brand: Partial<PrintHouseBrand>,
): string | null => {
  if (isHpClientTheme(brand.theme, brand.tenantId)) {
    return HP_CLIENT_THEME;
  }
  const theme = brand.theme?.trim();
  return theme ? theme : null;
};

let pendingBrand: Promise<PrintHouseBrand | null> | null = null;

const requestPrintHouseBrand = async (): Promise<PrintHouseBrand | null> => {
  try {
    const response: any = await getApiRequest(GET_PRINT_HOUSE_BRAND_URL, {});
    if (!response?.success) {
      return null;
    }
    return readPrintHouseBrand(response.data);
  } catch {
    return null;
  }
};

/**
 * Brand of the print house behind the current domain, for visitors without a token.
 * Shared across callers (theme provider, login and invite screens) so the lookup that
 * decides the branding happens once per page load. Failed lookups are not cached.
 */
export const fetchPrintHouseBrand = (): Promise<PrintHouseBrand | null> => {
  if (!pendingBrand) {
    pendingBrand = requestPrintHouseBrand().then((brand) => {
      if (!brand) {
        pendingBrand = null;
      }
      return brand;
    });
  }
  return pendingBrand;
};

/**
 * Theme of the print house behind the current domain. Once signed in, validate's
 * `printHouseProfile.theme` is the source of truth.
 */
export const fetchBrandTheme = async (): Promise<string | null> => {
  const brand = await fetchPrintHouseBrand();
  return brand ? resolveBrandThemeName(brand) : null;
};
